import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Stack, Button, TextField, MenuItem, Chip,
  Alert, CircularProgress, Switch, FormControlLabel, Snackbar, Autocomplete,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {
  getAppointmentTypes, getAppointmentType, updateAppointmentType, getUsers,
} from '../services/api';

const WEEKDAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

const DURATION_OPTIONS = [15, 30, 45, 60, 75, 90, 120];

export default function AppointmentTypesPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const [types, setTypes] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [users, setUsers] = useState([]);

  // Form state
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, uRes] = await Promise.all([getAppointmentTypes(), getUsers()]);
      setTypes(tRes.data || []);
      setUsers(uRes.data || []);
      if (tRes.data?.length && !activeKey) {
        setActiveKey(tRes.data[0].key);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load appointment types');
    } finally {
      setLoading(false);
    }
  }, [activeKey]);

  useEffect(() => { load(); }, [load]);

  // Load form when activeKey changes
  useEffect(() => {
    if (!activeKey) return;
    (async () => {
      try {
        const { data } = await getAppointmentType(activeKey);
        setForm(data);
      } catch (e) {
        setError('Failed to load type');
      }
    })();
  }, [activeKey]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleWeekday = (day) => {
    const set = new Set(form.workingDays || []);
    if (set.has(day)) set.delete(day); else set.add(day);
    update({ workingDays: [...set].sort((a, b) => a - b) });
  };

  const toggleDuration = (d) => {
    const set = new Set(form.allowedDurations || []);
    if (set.has(d)) set.delete(d); else set.add(d);
    update({ allowedDurations: [...set].sort((a, b) => a - b) });
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setError('');
    try {
      const hostIds = (form.defaultHosts || form.defaultHostUserIds || []).map((h) =>
        typeof h === 'object' ? h.id : h
      );

      const payload = {
        label: form.label,
        description: form.description || null,
        defaultDurationMinutes: Number(form.defaultDurationMinutes) || 30,
        allowedDurations: form.allowedDurations || [],
        leadTimeMinutes: Number(form.leadTimeMinutes) || 60,
        maxDaysAhead: Number(form.maxDaysAhead) || 21,
        maxReschedules: Number(form.maxReschedules) || 3,
        bufferMinutes: Number(form.bufferMinutes) || 0,
        workingDays: form.workingDays || [1, 2, 3, 4, 5],
        workStartUtc: form.workStartUtc || '08:00',
        workEndUtc: form.workEndUtc || '17:00',
        defaultHostUserId: hostIds[0] || null,
        defaultHostUserIds: hostIds,
        emailTemplatePrefix: form.emailTemplatePrefix || form.key,
        confirmationUrlTemplate: form.confirmationUrlTemplate || null,
        bookUrl: form.bookUrl || null,
        icsTitleTemplate: form.icsTitleTemplate || null,
        requireCompany: !!form.requireCompany,
        requireJobTitle: !!form.requireJobTitle,
        requirePhone: !!form.requirePhone,
        isActive: !!form.isActive,
      };
      const { data } = await updateAppointmentType(activeKey, payload);
      setForm(data);
      setToast({ severity: 'success', message: `${data.label} saved.` });
      // Refresh list to update sidebar host counts
      const tRes = await getAppointmentTypes();
      setTypes(tRes.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure how each appointment type behaves: durations, working hours, and which users
        host it. Slot suggestions check every listed host's calendar — a slot is offered only when
        all hosts are free.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {/* Left: type list */}
        <Paper sx={{ p: 1.5, width: { md: 260 }, alignSelf: 'flex-start' }}>
          <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>Types</Typography>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {types.map((t) => (
              <Button
                key={t.key}
                variant={activeKey === t.key ? 'contained' : 'text'}
                onClick={() => setActiveKey(t.key)}
                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
              >
                <Stack alignItems="flex-start" sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.label}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {t.key} · {t.defaultDurationMinutes}min · {(t.defaultHosts || []).length || 0} host(s)
                    {!t.isActive && ' · inactive'}
                  </Typography>
                </Stack>
              </Button>
            ))}
          </Stack>
        </Paper>

        {/* Right: form */}
        {form && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5">{form.label}</Typography>
                  <Typography variant="caption" color="text.secondary">key: {form.key}</Typography>
                </Box>
                <FormControlLabel
                  control={<Switch checked={!!form.isActive} onChange={(e) => update({ isActive: e.target.checked })} />}
                  label={form.isActive ? 'Active' : 'Inactive'}
                />
              </Stack>

              <TextField
                label="Label" value={form.label || ''} onChange={(e) => update({ label: e.target.value })}
                fullWidth size="small" sx={{ mt: 2 }}
              />
              <TextField
                label="Description" value={form.description || ''} onChange={(e) => update({ description: e.target.value })}
                fullWidth size="small" multiline minRows={2} sx={{ mt: 2 }}
              />
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Hosts</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Pick one or more MCRM users. The first selected host is the primary
                (Meeting creator + Lead assignment). Slot suggestions check every host's calendar.
              </Typography>
              <Autocomplete
                multiple
                options={users}
                value={(form.defaultHosts || []).map((h) =>
                  users.find((u) => u.id === (typeof h === 'object' ? h.id : h)) || h)
                  .filter(Boolean)}
                getOptionLabel={(u) => `${u.fullName || u.username} (${u.email})`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, val) => update({ defaultHosts: val, defaultHostUserIds: val.map((u) => u.id) })}
                renderInput={(p) => <TextField {...p} label="Hosts (in priority order)" placeholder="Add host" />}
                renderTags={(value, getTagProps) =>
                  value.map((u, idx) => (
                    <Chip
                      label={`${idx === 0 ? '★ ' : ''}${u.fullName || u.username}`}
                      {...getTagProps({ index: idx })}
                      key={u.id}
                      color={idx === 0 ? 'primary' : 'default'}
                    />
                  ))
                }
              />
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Duration</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select label="Default duration" size="small"
                  value={form.defaultDurationMinutes}
                  onChange={(e) => update({ defaultDurationMinutes: Number(e.target.value) })}
                  sx={{ minWidth: 180 }}
                >
                  {DURATION_OPTIONS.map((d) => (
                    <MenuItem key={d} value={d}>{d} min</MenuItem>
                  ))}
                </TextField>

                <Box>
                  <Typography variant="caption" color="text.secondary">Allowed durations (prospect picks)</Typography>
                  <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75, mt: 0.5 }}>
                    {DURATION_OPTIONS.map((d) => {
                      const on = (form.allowedDurations || []).includes(d);
                      return (
                        <Chip
                          key={d} label={`${d}m`} size="small"
                          onClick={() => toggleDuration(d)}
                          color={on ? 'primary' : 'default'}
                          variant={on ? 'filled' : 'outlined'}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              </Stack>
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Working hours (UTC)</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  type="time" label="Start (UTC)" size="small"
                  value={form.workStartUtc} onChange={(e) => update({ workStartUtc: e.target.value })}
                  slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 160 }}
                />
                <TextField
                  type="time" label="End (UTC)" size="small"
                  value={form.workEndUtc} onChange={(e) => update({ workEndUtc: e.target.value })}
                  slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 160 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Working days
              </Typography>
              <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75, mt: 0.5 }}>
                {WEEKDAYS.map((wd) => {
                  const on = (form.workingDays || []).includes(wd.value);
                  return (
                    <Chip
                      key={wd.value} label={wd.label} size="small"
                      onClick={() => toggleWeekday(wd.value)}
                      color={on ? 'primary' : 'default'}
                      variant={on ? 'filled' : 'outlined'}
                    />
                  );
                })}
              </Stack>
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Booking rules</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
                <TextField
                  type="number" label="Lead time (min)" size="small"
                  value={form.leadTimeMinutes}
                  onChange={(e) => update({ leadTimeMinutes: Number(e.target.value) })}
                  sx={{ width: 160 }}
                />
                <TextField
                  type="number" label="Max days ahead" size="small"
                  value={form.maxDaysAhead}
                  onChange={(e) => update({ maxDaysAhead: Number(e.target.value) })}
                  sx={{ width: 160 }}
                />
                <TextField
                  type="number" label="Buffer (min)" size="small"
                  value={form.bufferMinutes}
                  onChange={(e) => update({ bufferMinutes: Number(e.target.value) })}
                  sx={{ width: 160 }}
                />
                <TextField
                  type="number" label="Max reschedules" size="small"
                  value={form.maxReschedules}
                  onChange={(e) => update({ maxReschedules: Number(e.target.value) })}
                  sx={{ width: 160 }}
                />
              </Stack>

              <Stack direction="row" flexWrap="wrap" sx={{ gap: 1, mt: 2 }}>
                <FormControlLabel
                  control={<Switch checked={!!form.requireCompany} onChange={(e) => update({ requireCompany: e.target.checked })} />}
                  label="Require company"
                />
                <FormControlLabel
                  control={<Switch checked={!!form.requireJobTitle} onChange={(e) => update({ requireJobTitle: e.target.checked })} />}
                  label="Require job title"
                />
                <FormControlLabel
                  control={<Switch checked={!!form.requirePhone} onChange={(e) => update({ requirePhone: e.target.checked })} />}
                  label="Require phone"
                />
              </Stack>
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>URLs &amp; templates</Typography>
              <Stack spacing={2}>
                <TextField
                  label="Email template prefix" size="small"
                  value={form.emailTemplatePrefix || ''}
                  onChange={(e) => update({ emailTemplatePrefix: e.target.value })}
                  helperText={`Resolves to ${form.emailTemplatePrefix || form.key}_confirmed_prospect, _rescheduled_host, etc.`}
                />
                <TextField
                  label="Booking page URL"
                  value={form.bookUrl || ''}
                  onChange={(e) => update({ bookUrl: e.target.value })}
                  size="small"
                />
                <TextField
                  label="Confirmation URL template"
                  value={form.confirmationUrlTemplate || ''}
                  onChange={(e) => update({ confirmationUrlTemplate: e.target.value })}
                  size="small"
                  helperText="Use {token} where the booking token should be substituted."
                />
                <TextField
                  label="ICS title template"
                  value={form.icsTitleTemplate || ''}
                  onChange={(e) => update({ icsTitleTemplate: e.target.value })}
                  size="small"
                  helperText="e.g. 'Azotte demo — {{firstName}} {{lastName}}'"
                />
              </Stack>
            </Paper>

            <Stack direction="row" spacing={1}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </Stack>
          </Box>
        )}
      </Box>

      <Snackbar
        open={!!toast} autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? <Alert severity={toast.severity}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
